import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import usePageBodyClass from "../utils/usePageBodyClass";
import "./AdminDashboard.css";

const $ = window.$;

/*
 * React port of admin_dashboard.html. This page's original script is long
 * and almost entirely imperative DOM/jQuery code reading and writing
 * localStorage (rendering tables, charts, and PDF exports by id-based
 * lookups), so it is ported here inside a single effect exactly like the
 * original page's $(document).ready(...) block, rather than rewritten as
 * idiomatic per-widget React state — this keeps its behaviour identical to
 * the original rather than risking subtle regressions in such a large
 * rewrite. The only real changes from the original script are: the two
 * `window.location.href = 'admin_login.html'` redirects now call
 * React Router's `navigate()`, and the handful of document/window-level
 * event bindings plus the live-sync interval are namespaced so they can be
 * unbound when this component unmounts (a static page never needed that,
 * since the whole document was torn down on navigation).
 */
export default function AdminDashboard() {
  usePageBodyClass("page-admin-dashboard");
  const navigate = useNavigate();

  useEffect(() => {

  // ---------- Initialize Demo Data if Missing (Force v4 overwrite to ensure graphs populate) ----------
  function initDemoData() {
    if (!localStorage.getItem("dashboard_demo_v4")) {
      const demoSubmissions = [
        {id:"sub_1", candidateName:"Rahul Sharma", name:"Rahul Sharma", submittedAt:"2026-08-06T09:10:00", timestamp:"2026-08-06 09:10", totalQuestions:20, attemptedCount:18, notAttemptedCount:2, email:"rahul@gmail.com", score:18, percentage:90.0},
        {id:"sub_2", candidateName:"Priya Verma", name:"Priya Verma", submittedAt:"2026-08-06T09:20:00", timestamp:"2026-08-06 09:20", totalQuestions:20, attemptedCount:17, notAttemptedCount:3, email:"priya@gmail.com", score:17, percentage:85.0},
        {id:"sub_3", candidateName:"Aman Singh", name:"Aman Singh", submittedAt:"2026-08-06T09:35:00", timestamp:"2026-08-06 09:35", totalQuestions:20, attemptedCount:16, notAttemptedCount:4, email:"aman@gmail.com", score:12, percentage:60.0},
        {id:"sub_4", candidateName:"Neha Patel", name:"Neha Patel", submittedAt:"2026-08-06T09:50:00", timestamp:"2026-08-06 09:50", totalQuestions:20, attemptedCount:19, notAttemptedCount:1, email:"neha@gmail.com", score:19, percentage:95.0},
        {id:"sub_5", candidateName:"Rohit Kumar", name:"Rohit Kumar", submittedAt:"2026-08-06T10:05:00", timestamp:"2026-08-06 10:05", totalQuestions:20, attemptedCount:14, notAttemptedCount:6, email:"rohit@gmail.com", score:10, percentage:50.0},
        {id:"sub_6", candidateName:"Pooja Yadav", name:"Pooja Yadav", submittedAt:"2026-08-06T10:20:00", timestamp:"2026-08-06 10:20", totalQuestions:20, attemptedCount:20, notAttemptedCount:0, email:"pooja@gmail.com", score:20, percentage:100.0},
        {id:"sub_7", candidateName:"Vikas Gupta", name:"Vikas Gupta", submittedAt:"2026-08-06T10:35:00", timestamp:"2026-08-06 10:35", totalQuestions:20, attemptedCount:15, notAttemptedCount:5, email:"vikas@gmail.com", score:15, percentage:75.0},
        {id:"sub_8", candidateName:"Sneha Joshi", name:"Sneha Joshi", submittedAt:"2026-08-06T10:45:00", timestamp:"2026-08-06 10:45", totalQuestions:20, attemptedCount:18, notAttemptedCount:2, email:"sneha@gmail.com", score:18, percentage:90.0},
        {id:"sub_9", candidateName:"Arjun Mehta", name:"Arjun Mehta", submittedAt:"2026-08-06T11:00:00", timestamp:"2026-08-06 11:00", totalQuestions:20, attemptedCount:13, notAttemptedCount:7, email:"arjun@gmail.com", score:11, percentage:55.0},
        {id:"sub_10", candidateName:"Kiran Mishra", name:"Kiran Mishra", submittedAt:"2026-08-06T11:15:00", timestamp:"2026-08-06 11:15", totalQuestions:20, attemptedCount:12, notAttemptedCount:8, email:"kiran@gmail.com", score:8, percentage:40.0}
      ];
      localStorage.setItem("allExamSubmissions", JSON.stringify(demoSubmissions));
      localStorage.setItem("dashboard_demo_v4", "true"); // Ensures the new data populates
    }

    if (!localStorage.getItem("examList")) {
      const demoExams = [
        { id: "exam_demo1", examName: "General Aptitude Test", duration: 30, totalQuestions: 20, status: "Active", createdAt: "2026-08-01T09:00:00" },
        { id: "exam_demo2", examName: "Technical Round 1", duration: 45, totalQuestions: 25, status: "Active", createdAt: "2026-08-03T09:00:00" }
      ];
      localStorage.setItem("examList", JSON.stringify(demoExams));
    }

    if (!localStorage.getItem("secondLevelExams")) {
      const demoSecondExams = [
        { id: "sexam_demo1", examName: "Test Cases Round 1", duration: 60, testCases: 10, difficulty: "Medium", createdAt: "2026-08-05T09:00:00" }
      ];
      localStorage.setItem("secondLevelExams", JSON.stringify(demoSecondExams));
    }

    if (!localStorage.getItem("portalSettings")) {
      localStorage.setItem("portalSettings", JSON.stringify({ portalName: "Online Exam Portal", qualifyingPct: 70 }));
    }
  }

  // Inject required fields for calculations to ensure charts draw correctly
  initDemoData();

  // ---------- Default Question Bank Databases ----------
  const defaultQuestionsData = [
    { category: 'Aptitude', text: 'A train running at the speed of 60 km/hr crosses a pole in 9 seconds. What is the length of the train?', options: ['120 metres', '180 metres', '324 metres', '150 metres'], correctOption: 3 },
    { category: 'Aptitude', text: 'If A and B together can complete a piece of work in 15 days and B alone in 20 days, in how many days can A alone complete the work?', options: ['60 days', '45 days', '40 days', '30 days'], correctOption: 0 },
    { category: 'Aptitude', text: 'Find the average of all prime numbers between 30 and 50.', options: ['39.8', '38', '37', '39.25'], correctOption: 0 },
    { category: 'Aptitude', text: 'A vendor bought toffees at 6 for a rupee. How many for a rupee must he sell to gain 20%?', options: ['3', '4', '5', '6'], correctOption: 2 },
    { category: 'Aptitude', text: 'What percentage of numbers from 1 to 70 have 1 or 9 in the unit digit?', options: ['14%', '20%', '21%', '10%'], correctOption: 1 },
    { category: 'Reasoning', text: 'Look at this series: 2, 1, (1/2), (1/4), ... What number should come next?', options: ['(1/3)', '(1/8)', '(2/8)', '(1/16)'], correctOption: 1 },
    { category: 'Reasoning', text: 'Suresh is older than Jagan. Suresh is younger than Robert. Who is the oldest?', options: ['Suresh', 'Jagan', 'Robert', 'Data Inadequate'], correctOption: 2 },
    { category: 'Reasoning', text: 'Which word does NOT belong with the others?', options: ['Parsley', 'Basil', 'Dill', 'Mayonnaise'], correctOption: 3 },
    { category: 'Reasoning', text: 'Statements: All green are blue. All blue are white. Conclusion: All green are white.', options: ['Follows', 'Does not follow', 'Either or', 'Neither nor'], correctOption: 0 },
    { category: 'Reasoning', text: 'If CATER = 3120518, then how will DOG be written?', options: ['4157', '4147', '3157', '4158'], correctOption: 0 },
    { category: 'Language', text: 'Choose the synonym for "ABANDON":', options: ['Retain', 'Forsake', 'Adopt', 'Protect'], correctOption: 1 },
    { category: 'Language', text: 'Fill in the blank: "She has been living here ____ 2010."', options: ['for', 'from', 'since', 'by'], correctOption: 2 },
    { category: 'Language', text: 'Identify the correctly spelled word:', options: ['Accommodate', 'Acommodate', 'Accomodate', 'Acocommodate'], correctOption: 0 },
    { category: 'Language', text: 'Choose the antonym for "CANDID":', options: ['Frank', 'Deceitful', 'Honest', 'Sincere'], correctOption: 1 },
    { category: 'Language', text: 'Complete the idiom: "A blessing in _____"', options: ['disguise', 'trouble', 'darkness', 'reality'], correctOption: 0 },
    { category: 'Maths', text: 'What is the derivative of x² with respect to x?', options: ['x', '2x', 'x/2', '2'], correctOption: 1 },
    { category: 'Maths', text: 'Solve for x: 2x + 5 = 15', options: ['5', '10', '7.5', '2.5'], correctOption: 0 },
    { category: 'Maths', text: 'What is the area of a circle with radius 7 cm? (Use π = 22/7)', options: ['154 cm²', '44 cm²', '98 cm²', '308 cm²'], correctOption: 0 },
    { category: 'Maths', text: 'What is log₁₀(1000)?', options: ['1', '2', '3', '4'], correctOption: 2 },
    { category: 'Maths', text: 'What is the value of sin(90°)?', options: ['0', '1', '1/2', 'Undefined'], correctOption: 1 }
  ];

  const defaultTextsSet = new Set(defaultQuestionsData.map(q => q.text.trim()));
  let examQuestions = JSON.parse(localStorage.getItem('questionBank') || '[]');
  
  if (examQuestions.length < 20) {
    const existingTexts = new Set(examQuestions.map(q => q.text.trim()));
    defaultQuestionsData.forEach(dq => {
      if (!existingTexts.has(dq.text.trim())) {
        examQuestions.push({ id: 'q_' + Date.now() + Math.random(), ...dq });
      }
    });
    localStorage.setItem('questionBank', JSON.stringify(examQuestions));
  }

  // Round 2 Default Coding Questions
  const defaultCodingQuestionsData = [
        {
          id: 'q1', title: 'Sum of Array Elements', difficulty: 'Easy',
          description: 'Write a function that takes an array of integers and returns the sum of all the elements in the array.',
          examples: [{ input: '[1, 2, 3, 4, 5]', output: '15' }, { input: '[-1, -2, 3]', output: '0' }, { input: '[]', output: '0' }],
          funcName: 'sumArray',
          starterCode: { javascript: 'function sumArray(arr) {\n    // write your code here\n\n}', python: 'def sum_array(arr):\n    # write your code here\n    pass', cpp: '#include <iostream>\n#include <vector>\nusing namespace std;\n\nint sumArray(vector<int>& arr) {\n    // write your code here\n    return 0;\n}' },
          testCases: [{ input: [[1, 2, 3, 4, 5]], expected: 15, hidden: false }, { input: [[-1, -2, 3]], expected: 0, hidden: false }, { input: [[]], expected: 0, hidden: true }]
        },
        {
          id: 'q2', title: 'Reverse a String', difficulty: 'Easy',
          description: 'Write a function that takes a string and returns it reversed.',
          examples: [{ input: '"hello"', output: '"olleh"' }, { input: '"TCS"', output: '"SCT"' }, { input: '"racecar"', output: '"racecar"' }],
          funcName: 'reverseString',
          starterCode: { javascript: 'function reverseString(str) {\n    // write your code here\n\n}', python: 'def reverse_string(s):\n    # write your code here\n    pass', cpp: '#include <iostream>\n#include <string>\n#include <algorithm>\nusing namespace std;\n\nstring reverseString(string s) {\n    // write your code here\n    return "";\n}' },
          testCases: [{ input: ['hello'], expected: 'olleh', hidden: false }, { input: ['TCS'], expected: 'SCT', hidden: false }, { input: ['coding'], expected: 'gnidoc', hidden: true }]
        },
        {
          id: 'q3', title: 'Factorial of a Number', difficulty: 'Medium',
          description: 'Write a function that returns the factorial of a non-negative integer <code>n</code>. Factorial of 0 is 1.',
          examples: [{ input: '5', output: '120' }, { input: '0', output: '1' }, { input: '1', output: '1' }],
          funcName: 'factorial',
          starterCode: { javascript: 'function factorial(n) {\n    // write your code here\n\n}', python: 'def factorial(n):\n    # write your code here\n    pass', cpp: '#include <iostream>\nusing namespace std;\n\nlong long factorial(int n) {\n    // write your code here\n    return 1;\n}' },
          testCases: [{ input: [5], expected: 120, hidden: false }, { input: [0], expected: 1, hidden: false }, { input: [10], expected: 3628800, hidden: true }]
        },
        {
          id: 'q4', title: 'Find Maximum Element', difficulty: 'Easy',
          description: 'Write a function that finds and returns the maximum integer in a given array.',
          examples: [{ input: '[1, 5, 3, 9, 2]', output: '9' }, { input: '[-10, -3, -5]', output: '-3' }, { input: '[42]', output: '42' }],
          funcName: 'findMax',
          starterCode: { javascript: 'function findMax(arr) {\n    // write your code here\n\n}', python: 'def find_max(arr):\n    # write your code here\n    pass', cpp: '#include <iostream>\n#include <vector>\nusing namespace std;\n\nint findMax(vector<int>& arr) {\n    // write your code here\n    return 0;\n}' },
          testCases: [{ input: [[1, 5, 3, 9, 2]], expected: 9, hidden: false }, { input: [[-10, -3, -5]], expected: -3, hidden: false }, { input: [[42]], expected: 42, hidden: true }]
        },
        {
          id: 'q5', title: 'Check Palindrome', difficulty: 'Easy',
          description: 'Write a function that returns <code>true</code> if a given string is a palindrome, and <code>false</code> otherwise.',
          examples: [{ input: '"racecar"', output: 'true' }, { input: '"hello"', output: 'false' }, { input: '"a"', output: 'true' }],
          funcName: 'isPalindrome',
          starterCode: { javascript: 'function isPalindrome(str) {\n    // write your code here\n\n}', python: 'def is_palindrome(s):\n    # write your code here\n    pass', cpp: '#include <iostream>\n#include <string>\nusing namespace std;\n\nbool isPalindrome(string s) {\n    // write your code here\n    return false;\n}' },
          testCases: [{ input: ['racecar'], expected: true, hidden: false }, { input: ['hello'], expected: false, hidden: false }, { input: ['a'], expected: true, hidden: true }]
        },
        {
          id: 'q6', title: 'Count Vowels', difficulty: 'Easy',
          description: 'Write a function that takes a string and returns the number of vowels (a, e, i, o, u) present in it.',
          examples: [{ input: '"hello world"', output: '3' }, { input: '"rhythm"', output: '1' }, { input: '"aeiou"', output: '5' }],
          funcName: 'countVowels',
          starterCode: { javascript: 'function countVowels(str) {\n    // write your code here\n\n}', python: 'def count_vowels(s):\n    # write your code here\n    pass', cpp: '#include <iostream>\n#include <string>\nusing namespace std;\n\nint countVowels(string s) {\n    // write your code here\n    return 0;\n}' },
          testCases: [{ input: ['hello world'], expected: 3, hidden: false }, { input: ['rhythm'], expected: 1, hidden: false }, { input: ['aeiou'], expected: 5, hidden: true }]
        },
        {
          id: 'q7', title: 'Fibonacci Sequence', difficulty: 'Medium',
          description: 'Write a function to return the <code>n</code>th number in the Fibonacci sequence. Assume sequence starts: 0, 1, 1, 2, 3...',
          examples: [{ input: '5', output: '5' }, { input: '6', output: '8' }, { input: '0', output: '0' }],
          funcName: 'fibonacci',
          starterCode: { javascript: 'function fibonacci(n) {\n    // write your code here\n\n}', python: 'def fibonacci(n):\n    # write your code here\n    pass', cpp: '#include <iostream>\nusing namespace std;\n\nint fibonacci(int n) {\n    // write your code here\n    return 0;\n}' },
          testCases: [{ input: [5], expected: 5, hidden: false }, { input: [6], expected: 8, hidden: false }, { input: [0], expected: 0, hidden: true }]
        },
        {
          id: 'q8', title: 'Missing Number', difficulty: 'Medium',
          description: 'You are given an array of <code>n-1</code> integers containing numbers from 1 to <code>n</code>. Find the missing number.',
          examples: [{ input: '[1, 2, 4, 5], n=5', output: '3' }, { input: '[2, 3, 1, 5], n=5', output: '4' }, { input: '[1, 2, 3], n=4', output: '4' }],
          funcName: 'missingNumber',
          starterCode: { javascript: 'function missingNumber(arr, n) {\n    // write your code here\n\n}', python: 'def missing_number(arr, n):\n    # write your code here\n    pass', cpp: '#include <iostream>\n#include <vector>\nusing namespace std;\n\nint missingNumber(vector<int>& arr, int n) {\n    // write your code here\n    return 0;\n}' },
          testCases: [{ input: [[1, 2, 4, 5], 5], expected: 3, hidden: false }, { input: [[2, 3, 1, 5], 5], expected: 4, hidden: false }, { input: [[1, 2, 3], 4], expected: 4, hidden: true }]
        },
        {
          id: 'q9', title: 'Check Prime Number', difficulty: 'Easy',
          description: 'Write a function to check if a given integer is a prime number. Return <code>true</code> or <code>false</code>.',
          examples: [{ input: '7', output: 'true' }, { input: '10', output: 'false' }, { input: '2', output: 'true' }],
          funcName: 'isPrime',
          starterCode: { javascript: 'function isPrime(n) {\n    // write your code here\n\n}', python: 'def is_prime(n):\n    # write your code here\n    pass', cpp: '#include <iostream>\nusing namespace std;\n\nbool isPrime(int n) {\n    // write your code here\n    return false;\n}' },
          testCases: [{ input: [7], expected: true, hidden: false }, { input: [10], expected: false, hidden: false }, { input: [2], expected: true, hidden: true }]
        },
        {
          id: 'q10', title: 'Calculate Power', difficulty: 'Easy',
          description: 'Write a function that calculates <code>base</code> raised to the power of <code>exp</code>.',
          examples: [{ input: '2, 3', output: '8' }, { input: '5, 2', output: '25' }, { input: '10, 0', output: '1' }],
          funcName: 'power',
          starterCode: { javascript: 'function power(base, exp) {\n    // write your code here\n\n}', python: 'def power(base, exp):\n    # write your code here\n    pass', cpp: '#include <iostream>\nusing namespace std;\n\nlong long power(int base, int exp) {\n    // write your code here\n    return 1;\n}' },
          testCases: [{ input: [2, 3], expected: 8, hidden: false }, { input: [5, 2], expected: 25, hidden: false }, { input: [10, 0], expected: 1, hidden: true }]
        }
  ];

  let codingExamQuestions = JSON.parse(localStorage.getItem('codingQuestionBank') || '[]');
  if (codingExamQuestions.length === 0) {
      localStorage.setItem('codingQuestionBank', JSON.stringify(defaultCodingQuestionsData));
  }

  // ---------- Auth Guard ----------
  if (sessionStorage.getItem('adminSession') !== 'active') { 
    navigate('/admin/login'); 
    return;
  }

  const adminEmail = sessionStorage.getItem('adminEmail') || '';
  if (adminEmail) {
    $('#adminEmailDisplay').text(adminEmail);
    $('#settingsAdminEmail').text(adminEmail);
  } else {
    $('#adminEmailDisplay').hide();
  }

  function doLogout() {
    sessionStorage.removeItem('adminSession'); 
    sessionStorage.removeItem('adminEmail');
    navigate('/admin/login'); 
  }
  $('#logoutBtnTop, #logoutBtnSide').on('click', doLogout);

  // ---------- Settings Loader ----------
  function loadSettings() {
    try {
      return Object.assign(
        { portalName: 'Online Exam Portal', qualifyingPct: 70, round1AccessCode: 'WELCOME1' },
        JSON.parse(localStorage.getItem('portalSettings') || '{}')
      );
    } catch (e) {
      return { portalName: 'Online Exam Portal', qualifyingPct: 70, round1AccessCode: 'WELCOME1' };
    }
  }
  function saveSettings(s) { localStorage.setItem('portalSettings', JSON.stringify(s)); }

  // ---------- Access Codes ----------
  function generateAccessCode() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous 0/O or 1/I
    let code = '';
    for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
  }

  // ---------- Round 2 Invitations (per-candidate access codes, emailed out) ----------
  function loadRound2Invites() {
    try { return JSON.parse(localStorage.getItem('round2Invitations') || '{}'); } catch (e) { return {}; }
  }
  function saveRound2Invites(obj) { localStorage.setItem('round2Invitations', JSON.stringify(obj)); }

  function renderRound2Invites() {
    const qualifyingPct = Number(loadSettings().qualifyingPct) || 70;
    const results = loadResults();
    const invites = loadRound2Invites();

    const eligible = results.filter(function (r) {
      const pct = r.percentage !== undefined ? parseFloat(r.percentage) : 0;
      return !isNaN(pct) && pct >= qualifyingPct;
    });

    $('#round2EligibleBadge').text('Eligible: ' + eligible.length);

    const $tbody = $('#round2InviteTableBody');
    $tbody.empty();

    if (eligible.length === 0) {
      $('#round2InviteTableBody').closest('.table-responsive').hide();
      $('#round2InviteEmptyState').show();
      return;
    }
    $('#round2InviteTableBody').closest('.table-responsive').show();
    $('#round2InviteEmptyState').hide();

    eligible.slice().reverse().forEach(function (r) {
      const key = r.id || (r.name + '_' + r.timestamp);
      const email = r.email || '';
      const invite = invites[key];
      const code = invite ? invite.code : '—';

      let statusHtml = '<span class="badge-pill fail">Not Sent</span>';
      if (invite && invite.usedAt) {
        statusHtml = '<span class="badge-pill pass"><i class="fa-solid fa-check me-1"></i> Code Used</span>';
      } else if (invite && invite.sentAt) {
        statusHtml = '<span class="badge-pill attempted">Sent ' + formatDate(invite.sentAt) + '</span>';
      }

      const noEmail = !email;
      const sendLabel = invite && invite.sentAt ? 'Resend' : 'Send Invite';
      const sendDisabled = noEmail ? 'disabled title="No email on file for this candidate"' : '';

      $tbody.append(`
        <tr>
          <td class="name-cell">${r.name || r.candidateName || 'Unknown'}</td>
          <td>${email || '<span class="text-muted">N/A</span>'}</td>
          <td class="fw-bold">${r.percentage}%</td>
          <td><code>${code}</code></td>
          <td>${statusHtml}</td>
          <td class="text-end">
            <button type="button" class="btn-edit send-r2-invite me-1" data-key="${key}" ${sendDisabled}>
              <i class="fa-solid fa-paper-plane me-1"></i>${sendLabel}
            </button>
            <button type="button" class="btn-clear regen-r2-code" data-key="${key}" title="Generate a new code (does not send an email)">
              <i class="fa-solid fa-rotate"></i>
            </button>
          </td>
        </tr>`);
    });
  }

  $(document).on('click.pageAdminDash', '.send-r2-invite', function () {
    const key = $(this).data('key');
    const results = loadResults();
    const record = results.find(function (r) { return (r.id || (r.name + '_' + r.timestamp)) === String(key); });
    if (!record || !record.email) { alert('This candidate has no email on file.'); return; }

    const invites = loadRound2Invites();
    const existing = invites[key];
    const code = (existing && existing.code) || generateAccessCode();

    const settings = loadSettings();
    const portalName = settings.portalName || 'Online Exam Portal';
    const candName = record.name || record.candidateName || 'Candidate';
    const link = window.location.origin + '/second-level-exam?code=' + encodeURIComponent(code);

    invites[key] = {
      code: code,
      email: record.email,
      name: candName,
      sentAt: new Date().toISOString(),
      usedAt: existing ? existing.usedAt : null
    };
    saveRound2Invites(invites);

    const subject = encodeURIComponent(portalName + ' \u2014 Round 2 Exam Access');
    const body = encodeURIComponent(
      'Hello ' + candName + ',\n\n' +
      'Congratulations on clearing Round 1 of the ' + portalName + '!\n\n' +
      'You are invited to Round 2 (Technical / Coding Round).\n\n' +
      'Access Link: ' + link + '\n' +
      'Access Code: ' + code + '\n\n' +
      'Open the link above, fill in your details, and enter the Access Code exactly as shown when prompted, to unlock the Round 2 exam.\n\n' +
      'Best of luck!\n' +
      portalName + ' Team'
    );

    // No backend/email API is used here — this opens the admin's own email
    // client with the message pre-filled, so a real email goes out from
    // their own address once they hit send in that client.
    window.location.href = 'mailto:' + encodeURIComponent(record.email) + '?subject=' + subject + '&body=' + body;

    renderRound2Invites();
  });

  $(document).on('click.pageAdminDash', '.regen-r2-code', function () {
    const key = $(this).data('key');
    const invites = loadRound2Invites();
    const existing = invites[key];
    if (!confirm('Generate a new access code for this candidate? Any previously sent code will stop working, and you will need to Send/Resend the invite for them to receive the new one.')) return;
    invites[key] = Object.assign({}, existing, { code: generateAccessCode(), usedAt: null });
    saveRound2Invites(invites);
    renderRound2Invites();
  });

  // ---------- Load Candidate Results Data ----------
  function loadResults() {
    let results = [];
    try {
      results = JSON.parse(localStorage.getItem('allExamSubmissions') || '[]');
    } catch (e) {
      results = [];
    }
    if (results.length === 0) {
       try { results = JSON.parse(localStorage.getItem('allExamResults') || '[]'); } catch(e) {}
    }
    return results;
  }

  function formatDate(iso) {
    if (!iso) return '-';
    if (iso.includes(',') || iso.includes('/')) return iso;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // ---------- Safe Graphical Chart Rendering ----------
  let chartInstances = {}; // Global object to hold existing chart instances

  function renderChartSafe(canvasId, chartType, configData, configOptions) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;

    // Destroy existing instance to prevent visual overlaps or memory leaks
    if (chartInstances[canvasId]) {
      chartInstances[canvasId].destroy();
    }

    // Initialize new Chart strictly attached to the canvas element context
    chartInstances[canvasId] = new window.Chart(canvas, {
      type: chartType,
      data: configData,
      options: configOptions
    });
  }

  function renderCharts(results, qualifyingPct) {
    // Run chart execution with a small delay so container bounding boxes explicitly register in the DOM 
    // This prevents Chart.js from rendering 0x0 empty pixel maps when a tab is redrawn.
    setTimeout(() => {
      let passedCount = 0, failedCount = 0;
      let scoreBrackets = { '0 - 40%': 0, '41 - 70%': 0, '71 - 100%': 0 };
      let completionBuckets = { '0 - 25%': 0, '26 - 50%': 0, '51 - 75%': 0, '76 - 100%': 0 };
      let totalAttempted = 0, totalNotAttempted = 0;

      let hasData = results && results.length > 0;

      if (hasData) {
        results.forEach(function (r) {
          const total = r.totalQuestions || 20;
          let attempted = 0;
          if (r.userAnswers) attempted = Object.keys(r.userAnswers).length;
          else if (r.attemptedCount !== undefined) attempted = r.attemptedCount;

          const unattempted = total - attempted;
          totalAttempted += attempted;
          totalNotAttempted += unattempted;

          const compPct = total > 0 ? Math.round((attempted / total) * 100) : 0;
          if (compPct <= 25) completionBuckets['0 - 25%']++;
          else if (compPct <= 50) completionBuckets['26 - 50%']++;
          else if (compPct <= 75) completionBuckets['51 - 75%']++;
          else completionBuckets['76 - 100%']++;

          let scoreVal = r.score !== undefined ? r.score : attempted;
          let scorePct = r.percentage !== undefined ? parseFloat(r.percentage) : (total > 0 ? Math.round((scoreVal / total) * 100) : 0);

          if (scorePct >= qualifyingPct) passedCount++;
          else failedCount++;

          if (scorePct <= 40) scoreBrackets['0 - 40%']++;
          else if (scorePct <= 70) scoreBrackets['41 - 70%']++;
          else scoreBrackets['71 - 100%']++;
        });
      }

      // Empty State Placeholders: If you hit "Clear All Data", it still draws greyed out template graphs.
      const passData = hasData ? [passedCount, failedCount] : [1, 1];
      const passColors = hasData ? ['#16a34a', '#dc2626'] : ['#e2e8f0', '#cbd5e1'];
      const passLabels = hasData ? ['Passed', 'Failed'] : ['No Data', 'No Data'];

      const barScoreData = hasData ? Object.values(scoreBrackets) : [1, 2, 1];
      const barScoreColors = hasData ? ['#ef4444', '#f59e0b', '#10b981'] : ['#e2e8f0', '#cbd5e1', '#e2e8f0'];

      const compData = hasData ? Object.values(completionBuckets) : [1, 2, 1, 3];
      const compColors = hasData ? '#0284c7' : '#e2e8f0';

      const attemptData = hasData ? [totalAttempted, totalNotAttempted] : [1, 1];
      const attemptColors = hasData ? ['#9333ea', '#cbd5e1'] : ['#e2e8f0', '#f1f5f9'];

      // Chart 1: Pass vs Fail Ratio
      renderChartSafe('chartPassFail', 'doughnut', {
        labels: passLabels,
        datasets: [{
          data: passData,
          backgroundColor: passColors,
          borderWidth: 3, 
          borderColor: '#ffffff',
          hoverOffset: 4, 
          cutout: '65%'
        }]
      }, {
        responsive: true, 
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { font: { weight: 'bold' } } } }
      });

      // Chart 2: Score Performance Brackets
      renderChartSafe('chartScoreDistribution', 'bar', {
        labels: ['0 - 40%', '41 - 70%', '71 - 100%'],
        datasets: [{
          label: 'Candidates',
          data: barScoreData,
          backgroundColor: barScoreColors,
          borderRadius: 8, 
          maxBarThickness: 45
        }]
      }, {
        responsive: true, 
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { 
          y: { beginAtZero: true, ticks: { precision: 0 } },
          x: { grid: { display: false } }
        }
      });

      // Chart 3: Completion Buckets
      renderChartSafe('chartCompletionBuckets', 'bar', {
        labels: ['0-25%', '26-50%', '51-75%', '76-100%'],
        datasets: [{
          label: 'Candidates',
          data: compData,
          backgroundColor: compColors,
          borderRadius: 8, 
          maxBarThickness: 45
        }]
      }, {
        responsive: true, 
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { 
          y: { beginAtZero: true, ticks: { precision: 0 } },
          x: { grid: { display: false } }
        }
      });

      // Chart 4: Attempt Ratio
      renderChartSafe('chartAttemptRatio', 'doughnut', {
        labels: hasData ? ['Attempted Qs', 'Unattempted Qs'] : ['No Data', 'No Data'],
        datasets: [{
          data: attemptData,
          backgroundColor: attemptColors,
          borderWidth: 3, 
          borderColor: '#ffffff',
          hoverOffset: 4, 
          cutout: '65%'
        }]
      }, {
        responsive: true, 
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { font: { weight: 'bold' } } } }
      });

      // ---------- Derived per-candidate rows (Students + Results Report data) ----------
      const rows = (results || []).map(function (r) {
        const total = r.totalQuestions || 20;
        let attempted = 0;
        if (r.userAnswers) attempted = Object.keys(r.userAnswers).length;
        else if (r.attemptedCount !== undefined) attempted = r.attemptedCount;
        const scoreVal = r.score !== undefined ? r.score : attempted;
        const pct = r.percentage !== undefined
          ? parseFloat(r.percentage)
          : (total > 0 ? Math.round((scoreVal / total) * 100) : 0);
        return {
          name: r.candidateName || r.name || 'Unknown',
          when: r.submittedAt || r.timestamp,
          pct: isNaN(pct) ? 0 : pct
        };
      });

      const totalQs = totalAttempted + totalNotAttempted;
      const passRate = hasData && (passedCount + failedCount) > 0
        ? Math.round((passedCount / (passedCount + failedCount)) * 100) : 0;
      const attemptRate = totalQs > 0 ? Math.round((totalAttempted / totalQs) * 100) : 0;

      $('#passRateValue').text(hasData ? passRate + '%' : '--');
      $('#attemptRateValue').text(hasData ? attemptRate + '%' : '--');

      $('#captionPassFail').html(hasData
        ? `<span>Passed: <b class="text-success">${passedCount}</b></span>
           <span>Failed: <b class="text-danger">${failedCount}</b></span>
           <span>Criterion: <b>&ge; ${qualifyingPct}%</b></span>`
        : '<span>No submissions yet</span>');

      $('#captionScore').html(hasData
        ? `<span>Low: <b>${scoreBrackets['0 - 40%']}</b></span>
           <span>Mid: <b>${scoreBrackets['41 - 70%']}</b></span>
           <span>High: <b>${scoreBrackets['71 - 100%']}</b></span>`
        : '<span>No submissions yet</span>');

      $('#captionCompletion').html(hasData
        ? `<span>Candidates: <b>${rows.length}</b></span>
           <span>Mostly complete (76-100%): <b>${completionBuckets['76 - 100%']}</b></span>`
        : '<span>No submissions yet</span>');

      $('#captionAttempt').html(hasData
        ? `<span>Attempted: <b>${totalAttempted}</b></span>
           <span>Unattempted: <b>${totalNotAttempted}</b></span>
           <span>Total Qs: <b>${totalQs}</b></span>`
        : '<span>No submissions yet</span>');

      // Chart 5: Round 1 -> Round 2 funnel
      const funnelData = hasData
        ? [rows.length, passedCount, failedCount]
        : [3, 2, 1];
      renderChartSafe('chartRoundFunnel', 'bar', {
        labels: ['Appeared (Round 1)', 'Eligible (Round 2)', 'Not Qualified'],
        datasets: [{
          label: 'Candidates',
          data: funnelData,
          backgroundColor: hasData ? ['#0284c7', '#16a34a', '#dc2626'] : ['#e2e8f0', '#cbd5e1', '#e2e8f0'],
          borderRadius: 8,
          maxBarThickness: 34
        }]
      }, {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, ticks: { precision: 0 } },
          y: { grid: { display: false } }
        }
      });
      $('#captionFunnel').html(hasData
        ? `<span>Round 2 shortlisting rate: <b>${passRate}%</b> of Round 1 candidates</span>`
        : '<span>No submissions yet</span>');

      // Chart 6: Top 5 performers
      const top = rows.slice().sort(function (a, b) { return b.pct - a.pct; }).slice(0, 5);
      renderChartSafe('chartTopPerformers', 'bar', {
        labels: top.length ? top.map(function (t) { return t.name; }) : ['No Data', 'No Data', 'No Data'],
        datasets: [{
          label: 'Score %',
          data: top.length ? top.map(function (t) { return t.pct; }) : [1, 2, 3],
          backgroundColor: top.length
            ? top.map(function (t) { return t.pct >= qualifyingPct ? '#16a34a' : '#f59e0b'; })
            : '#e2e8f0',
          borderRadius: 8,
          maxBarThickness: 26
        }]
      }, {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, max: 100, ticks: { callback: function (v) { return v + '%'; } } },
          y: { grid: { display: false } }
        }
      });
      $('#captionTop').html(top.length
        ? `<span>Highest score: <b>${top[0].name} (${top[0].pct}%)</b></span>`
        : '<span>No submissions yet</span>');

      // Chart 7: Score trend across submissions
      const chrono = rows.slice().sort(function (a, b) {
        return new Date(a.when || 0) - new Date(b.when || 0);
      });
      const trendLabels = chrono.length
        ? chrono.map(function (t) { return t.when ? formatDate(t.when) : t.name; })
        : ['—', '—', '—', '—'];
      const trendValues = chrono.length ? chrono.map(function (t) { return t.pct; }) : [0, 0, 0, 0];
      const avgPct = chrono.length
        ? Math.round(trendValues.reduce(function (a, b) { return a + b; }, 0) / chrono.length) : 0;

      renderChartSafe('chartScoreTrend', 'line', {
        labels: trendLabels,
        datasets: [
          {
            label: 'Candidate Score %',
            data: trendValues,
            borderColor: '#0284c7',
            backgroundColor: 'rgba(2,132,199,0.12)',
            fill: true,
            tension: 0.35,
            pointRadius: 4,
            pointBackgroundColor: '#0284c7'
          },
          {
            label: 'Passing Criterion',
            data: trendLabels.map(function () { return qualifyingPct; }),
            borderColor: '#dc2626',
            borderDash: [6, 6],
            pointRadius: 0,
            fill: false,
            tension: 0
          }
        ]
      }, {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { font: { weight: 'bold' } } } },
        scales: {
          y: { beginAtZero: true, max: 100, ticks: { callback: function (v) { return v + '%'; } } },
          x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true } }
        }
      });
      $('#captionTrend').html(chrono.length
        ? `<span>Average score: <b>${avgPct}%</b></span>
           <span>Best: <b>${Math.max.apply(null, trendValues)}%</b></span>
           <span>Lowest: <b>${Math.min.apply(null, trendValues)}%</b></span>
           <span>Submissions plotted: <b>${chrono.length}</b></span>`
        : '<span>No submissions yet</span>');

    }, 50); // 50ms buffer

  }


  // ---------- Render Main Dashboard Data ----------
  function renderDashboard() {
    const results = loadResults();
    const settings = loadSettings();
    const qualifyingPct = Number(settings.qualifyingPct) || 70;

    $('#dashPassingBadge').text('Passing Criterion: ≥ ' + qualifyingPct + '%');
    $('#reportQualifyingThresholdText').text('≥ ' + qualifyingPct + '% Score');

    const totalCandidates = results.length;
    let totalAttempted = 0;
    let totalPending = 0;
    let completionSum = 0;
    let totalPassed = 0;
    let totalFailed = 0;

    const $reportTbody = $('#reportTableBody');
    const $dashRecentTbody = $('#dashboardRecentTableBody');
    const $reportWrapper = $('#reportTableBody').closest('.table-responsive');

    $reportTbody.empty();
    $dashRecentTbody.empty();

    if (totalCandidates === 0) {
      $reportWrapper.hide();
      $('#reportEmptyState').show();
      $dashRecentTbody.html('<tr><td colspan="5" class="text-center text-muted py-4">No submissions recorded yet.</td></tr>');
    } else {
      $reportWrapper.show();
      $('#reportEmptyState').hide();

      const ordered = results.slice().reverse();

      ordered.forEach(function (r, idx) {
        const total = r.totalQuestions || 20;
        
        let attempted = 0;
        if (r.userAnswers) attempted = Object.keys(r.userAnswers).length;
        else if (r.attemptedCount !== undefined) attempted = r.attemptedCount;

        const notAttempted = total - attempted;
        let scoreVal = r.score !== undefined ? r.score : attempted;
        let pct = r.percentage;
        if (pct === undefined) {
             pct = total > 0 ? Math.round((scoreVal / total) * 100) : 0;
        } else {
             pct = parseFloat(pct);
        }

        const isPassed = pct >= qualifyingPct;
        if (isPassed) totalPassed++;
        else totalFailed++;

        totalAttempted += attempted;
        totalPending += notAttempted;
        completionSum += parseFloat(pct);

        const candName = r.candidateName || r.name || 'Unknown';
        const timestamp = r.submittedAt || r.timestamp;

        // (Students tab now lists login-form details only; see renderStudents())


        // 2. Render Results Report Table Row
        const passBadge = isPassed 
          ? `<span class="badge-pill pass"><i class="fa-solid fa-check me-1"></i> PASSED</span>`
          : `<span class="badge-pill fail"><i class="fa-solid fa-xmark me-1"></i> FAILED</span>`;

        const reportRow = `
          <tr>
            <td class="name-cell">${candName}</td>
            <td>${formatDate(timestamp)}</td>
            <td><span class="badge-pill attempted">${attempted}</span></td>
            <td><span class="badge-pill not-attempted">${notAttempted}</span></td>
            <td>${total}</td>
            <td class="fw-bold">${scoreVal} / ${total}</td>
            <td class="fw-bold">${pct}%</td>
            <td>${passBadge}</td>
          </tr>`;
        $reportTbody.append(reportRow);

        // 3. Render Dashboard Recent Table Row (Top 5)
        if (idx < 5) {
          const recentRow = `
            <tr>
              <td class="name-cell">${candName}</td>
              <td class="small text-muted">${formatDate(timestamp)}</td>
              <td>${attempted} / ${total}</td>
              <td class="fw-bold">${pct}%</td>
              <td>${passBadge}</td>
            </tr>`;
          $dashRecentTbody.append(recentRow);
        }
      });
    }

    // Update Live Dashboard Stat Cards
    $('#statTotalCandidates').text(totalCandidates);
    $('#statTotalPassed').text(totalPassed);
    $('#statTotalFailed').text(totalFailed);
    $('#statTotalAttempted').text(totalAttempted);
    $('#statAvgPercent').text(totalCandidates > 0 ? Math.round(completionSum / totalCandidates) + '%' : '0%');
    
    // Update Results Report Summary Badges
    $('#reportPassBadgeCount').html(`<i class="fa-solid fa-check-circle me-1"></i> Passed: ${totalPassed}`);
    $('#reportFailBadgeCount').html(`<i class="fa-solid fa-xmark-circle me-1"></i> Failed: ${totalFailed}`);

    $('#settingsTotalCandidates').text(totalCandidates);

    // Call Chart initialization passing the calculated values
    renderCharts(results, qualifyingPct);
  }

  // ---------- Sidebar Navigation Event ----------
  $('.sidebar .menu-item[data-target]').on('click', function () {
    const target = $(this).data('target');
    $('.sidebar .menu-item[data-target]').removeClass('active');
    $(this).addClass('active');
    $('.page-section').removeClass('active');
    $('#page-' + target).addClass('active');
    $('#studentDetailPanel').removeClass('show');

    // Re-paint charts dynamically when coming back to the Dashboard tab 
    if (target === 'dashboard') {
      renderDashboard();
    }
  });

  $('#examMenuToggle').off('click.examToggle').on('click.examToggle', function() {
    $(this).find('i').toggleClass('fa-chevron-down fa-chevron-up');
    $(this).next('.sub-menu-list').stop(true, true).slideToggle(200);
  });

  $('#clearDataBtn').on('click', function () {
    if (confirm('Are you sure you want to clear all student details and candidate result data? This cannot be undone.')) {
      localStorage.removeItem('allExamResults');
      localStorage.removeItem('allExamSubmissions');
      localStorage.removeItem('registeredStudents');
      localStorage.removeItem('secondLevelResults');
      localStorage.removeItem('round2Invitations');
      renderDashboard();
      renderStudents();
      renderRound2Results();
      renderRound2Invites();
    }
  });

  // ---------- Exam Management Round 1 ----------
  function loadExamList() {
    try { return JSON.parse(localStorage.getItem('examList') || '[]'); } catch (e) { return []; }
  }
  function saveExamList(list) { localStorage.setItem('examList', JSON.stringify(list)); }

  function renderExamManagement() {
    const list = loadExamList();
    const $tbody = $('#examListTableBody');
    $tbody.empty();

    if (list.length === 0) {
      $('#page-examManagement .table-responsive').hide();
      $('#examListEmptyState').show();
    } else {
      $('#page-examManagement .table-responsive').show();
      $('#examListEmptyState').hide();

      list.slice().reverse().forEach(function (ex) {
        const row = $(`
          <tr>
            <td class="name-cell">${ex.examName}</td>
            <td>${ex.duration} mins</td>
            <td>${ex.totalQuestions}</td>
            <td>
              <select class="form-select form-select-sm status-select" data-id="${ex.id}" style="width:110px;display:inline-block;">
                <option value="Active" ${ex.status === 'Active' ? 'selected' : ''}>Active</option>
                <option value="Inactive" ${ex.status === 'Inactive' ? 'selected' : ''}>Inactive</option>
              </select>
            </td>
            <td>${formatDate(ex.createdAt)}</td>
            <td><button type="button" class="btn-clear" data-id="${ex.id}">Delete</button></td>
          </tr>`);
        row.find('.status-select').on('change', function () {
          const id = $(this).data('id');
          const newStatus = $(this).val();
          saveExamList(loadExamList().map(x => x.id === id ? { ...x, status: newStatus } : x));
        });
        row.find('.btn-clear').on('click', function () {
          const id = $(this).data('id');
          saveExamList(loadExamList().filter(x => x.id !== id));
          renderExamManagement();
          renderSettingsOverview();
        });
        $tbody.append(row);
      });
    }
  }

  $('#examCreateForm').on('submit', function (e) {
    e.preventDefault();
    const selectedExamName = $('#examNameInput').val().trim();
    if (!selectedExamName) return;
    const list = loadExamList();
    list.push({
      id: 'exam_' + Date.now(),
      examName: selectedExamName,
      duration: $('#examDurationInput').val(),
      totalQuestions: $('#examQuestionsInput').val(),
      status: $('#examStatusInput').val(),
      createdAt: new Date().toISOString()
    });
    saveExamList(list);
    $('#qCategory').val(selectedExamName);
    this.reset();
    renderCategoryOptions();
    renderExamManagement();
    renderSettingsOverview();
  });

  // Typing an exam name auto-fills the Question Bank category field
  $('#examNameInput').on('input change', function () {
    const val = $(this).val().trim();
    if (val) {
      $('#qCategory').val(val).trigger('change');
    }
  });

  // Keep the category suggestions in sync with the created exams
  function renderCategoryOptions() {
    const names = [...new Set(loadExamList().map(e => e.examName).filter(Boolean))];
    $('#examCategoryOptions').html(names.map(n => `<option value="${n}"></option>`).join(''));
  }

  // ---------- Question Bank Logic ----------
  function renderQBank() {
    const qBank = JSON.parse(localStorage.getItem('questionBank') || '[]');
    let defaultHtml = '';
    let newHtml = '';
    
    qBank.forEach(q => {
      const correctLetter = String.fromCharCode(65 + parseInt(q.correctOption));
      const isDefault = defaultTextsSet.has(q.text.trim());
      
      if (!isDefault) {
        newHtml += `
        <tr style="border-bottom: 1px solid #f1f5f9;">
          <td class="align-middle py-3 fw-medium text-dark" style="max-width: 350px;">${q.text}</td>
          <td class="align-middle py-3 text-secondary small" style="line-height: 1.8;">
            <div>A) ${q.options[0]}</div><div>B) ${q.options[1]}</div><div>C) ${q.options[2]}</div><div>D) ${q.options[3]}</div>
          </td>
          <td class="align-middle py-3 text-center">
            <span class="badge-pill attempted text-uppercase fs-6">${correctLetter}</span>
          </td>
          <td class="align-middle py-3 text-end">
            <button type="button" class="btn-edit mb-1 edit-q" data-id="${q.id}">Edit</button>
            <button type="button" class="btn-clear del-q" data-id="${q.id}">Delete</button>
          </td>
        </tr>`;
      } else {
        defaultHtml += `<tr>
          <td><span class="badge bg-dark text-white p-1 rounded small">${q.category}</span></td>
          <td style="font-size:0.85rem;">${q.text}</td>
          <td class="small text-muted">A: ${q.options[0]}<br>B: ${q.options[1]}<br>C: ${q.options[2]}<br>D: ${q.options[3]}</td>
          <td><span class="badge-pill attempted">${correctLetter}</span></td>
          <td>
            <button type="button" class="btn-edit mb-1 edit-q" data-id="${q.id}">Edit</button>
            <button type="button" class="btn-clear del-q" data-id="${q.id}">Delete</button>
          </td>
        </tr>`;
      }
    });

    if (defaultHtml === '') defaultHtml = '<tr><td colspan="5" class="text-center py-4 text-muted">No default questions found.</td></tr>';
    if (newHtml === '') newHtml = '<tr><td colspan="4" class="text-center py-4 text-muted">No custom questions added yet. Questions added will instantly appear here.</td></tr>';

    $('#defaultQBankTableBody').html(defaultHtml);
    $('#newQBankTableBody').html(newHtml);
  }

  $('#qBankForm').off('submit').on('submit', function(e) {
    e.preventDefault();
    let bank = JSON.parse(localStorage.getItem('questionBank') || '[]');
    const editId = $('#editQId').val();

    const newQ = {
      category: $('#qCategory').val(),
      text: $('#qText').val().trim(),
      options: [$('#optA').val(), $('#optB').val(), $('#optC').val(), $('#optD').val()],
      correctOption: parseInt($('#qCorrect').val())
    };

    if (editId) {
      const idx = bank.findIndex(q => q.id === editId);
      if (idx !== -1) bank[idx] = { id: editId, ...newQ };
      $('#qSuccessMsg').text('Question updated successfully!').fadeIn().delay(2000).fadeOut();
      $('#qFormTitle').text('Add MCQ to Database');
      $('#qSubmitBtn').text('+ Add Question');
      $('#editQId').val('');
    } else {
      newQ.id = 'q_' + Date.now();
      bank.push(newQ);
      $('#qSuccessMsg').text('Question added successfully!').fadeIn().delay(2000).fadeOut();
    }

    localStorage.setItem('questionBank', JSON.stringify(bank));
    this.reset(); 
    renderQBank();
  });

  $(document).on('click.pageAdminDash', '.edit-q', function() {
    const id = $(this).data('id');
    const bank = JSON.parse(localStorage.getItem('questionBank') || '[]');
    const q = bank.find(x => x.id === id);
    
    if (q) {
      $('#editQId').val(q.id);
      $('#qCategory').val(q.category);
      $('#qText').val(q.text);
      $('#optA').val(q.options[0]);
      $('#optB').val(q.options[1]);
      $('#optC').val(q.options[2]);
      $('#optD').val(q.options[3]);
      $('#qCorrect').val(q.correctOption);

      $('#qFormTitle').text('Edit MCQ');
      $('#qSubmitBtn').text('Update Question');
      window.scrollTo({ top: $('#qBankForm').offset().top - 100, behavior: 'smooth' });
    }
  });

  $(document).on('click.pageAdminDash', '.del-q', function() {
    const id = $(this).data('id');
    let bank = JSON.parse(localStorage.getItem('questionBank') || '[]');
    bank = bank.filter(q => q.id !== id);
    localStorage.setItem('questionBank', JSON.stringify(bank));
    renderQBank();
  });

  // ---------- Round 2 Config Management ----------
  function loadSecondExamList() {
    try { return JSON.parse(localStorage.getItem('secondLevelExams') || '[]'); } catch (e) { return []; }
  }
  function saveSecondExamList(list) { localStorage.setItem('secondLevelExams', JSON.stringify(list)); }

  function renderSecondExamManagement() {
    const list = loadSecondExamList();
    const $tbody = $('#secondExamListTableBody');
    $tbody.empty();

    if (list.length === 0) {
      $('#secondExamListTableBody').closest('.table-responsive').hide();
      $('#secondExamListEmptyState').show();
    } else {
      $('#secondExamListTableBody').closest('.table-responsive').show();
      $('#secondExamListEmptyState').hide();

      list.slice().reverse().forEach(function (ex) {
        const row = $(`
          <tr>
            <td class="name-cell">${ex.examName}</td>
            <td>${ex.duration} mins</td>
            <td>${ex.testCases}</td>
            <td><span class="badge-pill attempted">${ex.difficulty}</span></td>
            <td>${formatDate(ex.createdAt)}</td>
            <td><button type="button" class="btn-clear" data-id="${ex.id}">Delete</button></td>
          </tr>`);
        row.find('button').on('click', function () {
          saveSecondExamList(loadSecondExamList().filter(x => x.id !== $(this).data('id')));
          renderSecondExamManagement();
          renderSettingsOverview();
        });
        $tbody.append(row);
      });
    }
  }

  $('#secondExamCreateForm').on('submit', function (e) {
    e.preventDefault();
    const list = loadSecondExamList();
    list.push({
      id: 'sexam_' + Date.now(),
      examName: $('#secondExamNameInput').val().trim(),
      duration: $('#secondExamDurationInput').val(),
      testCases: $('#secondExamCasesInput').val(),
      difficulty: $('#secondExamDifficultyInput').val(),
      createdAt: new Date().toISOString()
    });
    saveSecondExamList(list);
    this.reset();
    renderSecondExamManagement();
    renderSettingsOverview();
  });

  // ---------- Round 2 Coding Questions ----------
  function renderCodingQBank() {
      const bank = JSON.parse(localStorage.getItem('codingQuestionBank') || '[]');
      const $tbody = $('#codingQTableBody');
      $tbody.empty();

      if(bank.length === 0) {
          $tbody.html('<tr><td colspan="5" class="text-center text-muted py-3">No coding questions added.</td></tr>');
          return;
      }

      bank.slice().reverse().forEach(q => {
          let hasExamples = q.examples && q.examples.length >= 3;
          let ex1In = hasExamples ? q.examples[0].input : 'N/A';
          let ex1Out = hasExamples ? q.examples[0].output : 'N/A';
          let ex2In = hasExamples ? q.examples[1].input : 'N/A';
          let ex2Out = hasExamples ? q.examples[1].output : 'N/A';
          let ex3In = hasExamples ? q.examples[2].input : 'N/A';
          let ex3Out = hasExamples ? q.examples[2].output : 'N/A';

          $tbody.append(`
            <tr>
              <td class="fw-bold text-dark" style="max-width: 200px;">${q.title}</td>
              <td><span class="badge-pill attempted">${q.difficulty}</span></td>
              <td><code>${q.correctOutput || q.funcName}</code></td>
              <td class="small text-muted" style="line-height: 1.6;">
                <div><span class="fw-bold">Ex1:</span> In: ${ex1In} | Out: ${ex1Out}</div>
                <div><span class="fw-bold">Ex2:</span> In: ${ex2In} | Out: ${ex2Out}</div>
                <div><span class="fw-bold">Ex3:</span> In: ${ex3In} | Out: ${ex3Out}</div>
              </td>
              <td>
                <button class="btn-edit edit-cq me-1" data-id="${q.id}">Edit</button>
                <button class="btn-clear del-cq" data-id="${q.id}">Delete</button>
              </td>
            </tr>
          `);
      });
  }

  $(document).on('click.pageAdminDash', '.edit-cq', function() {
      const id = $(this).data('id');
      const bank = JSON.parse(localStorage.getItem('codingQuestionBank') || '[]');
      const q = bank.find(x => x.id === id);
      
      if(q) {
          $('#editCqId').val(q.id);
          $('#cqTitle').val(q.title);
          $('#cqDiff').val(q.difficulty);
          $('#cqCorrectOutput').val(q.correctOutput || '');
          $('#cqDesc').val(q.description);

          if(q.examples && q.examples.length >= 3) {
             $('#cqEx1In').val(q.examples[0].input);
             $('#cqEx1Out').val(q.examples[0].output);
             $('#cqEx2In').val(q.examples[1].input);
             $('#cqEx2Out').val(q.examples[1].output);
             $('#cqEx3In').val(q.examples[2].input);
             $('#cqEx3Out').val(q.examples[2].output);
          }

          $('#codingQForm button[type="submit"]').text('Update Coding Problem');
          window.scrollTo({ top: $('#codingQForm').offset().top - 100, behavior: 'smooth' });
      }
  });

  $(document).on('click.pageAdminDash', '.del-cq', function() {
      const id = $(this).data('id');
      let bank = JSON.parse(localStorage.getItem('codingQuestionBank') || '[]');
      bank = bank.filter(q => q.id !== id);
      localStorage.setItem('codingQuestionBank', JSON.stringify(bank));
      renderCodingQBank();
  });

  $('#codingQForm').on('submit', function(e) {
      e.preventDefault();
      let bank = JSON.parse(localStorage.getItem('codingQuestionBank') || '[]');
      const editId = $('#editCqId').val();
      
      let title = $('#cqTitle').val().trim();
      let funcName = (title.replace(/[^a-zA-Z0-9 ]/g, '').split(/\s+/).map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('')) || ('solution' + Date.now());
      let correctOutput = $('#cqCorrectOutput').val().trim();
      
      let ex1In = $('#cqEx1In').val().trim();
      let ex1Out = $('#cqEx1Out').val().trim();
      let ex2In = $('#cqEx2In').val().trim();
      let ex2Out = $('#cqEx2Out').val().trim();
      let ex3In = $('#cqEx3In').val().trim();
      let ex3Out = $('#cqEx3Out').val().trim();
      
      function parseArg(val) { try { return JSON.parse('[' + val + ']'); } catch(e) { return [val]; } }
      function parseOut(val) { try { return JSON.parse(val); } catch(e) { return val; } }

      let newQ = {
          title: title,
          difficulty: $('#cqDiff').val(),
          description: $('#cqDesc').val().trim(),
          funcName: funcName,
          correctOutput: correctOutput,
          examples: [ 
             { input: ex1In, output: ex1Out },
             { input: ex2In, output: ex2Out },
             { input: ex3In, output: ex3Out }
          ],
          starterCode: {
              javascript: `function ${funcName}(args) {\n    // write your code here\n\n}`,
              python: `def ${funcName}(args):\n    # write your code here\n    pass`,
              cpp: `#include <iostream>\nusing namespace std;\n\nvoid ${funcName}() {\n    // write your code here\n}`
          },
          testCases: [ 
             { input: parseArg(ex1In), expected: parseOut(ex1Out), hidden: false },
             { input: parseArg(ex2In), expected: parseOut(ex2Out), hidden: false },
             { input: parseArg(ex3In), expected: parseOut(ex3Out), hidden: false } 
          ]
      };

      if (editId) {
          const idx = bank.findIndex(q => q.id === editId);
          if (idx !== -1) bank[idx] = { id: editId, ...newQ };
          $('#editCqId').val('');
          $(this).find('button[type="submit"]').text('+ Add Coding Problem');
      } else {
          newQ.id = 'cq_' + Date.now();
          bank.push(newQ);
      }

      localStorage.setItem('codingQuestionBank', JSON.stringify(bank));
      this.reset();
      renderCodingQBank();
  });

  // ---------- Settings Overview ----------
  function renderSettingsOverview() {
    $('#settingsTotalExams').text(loadExamList().length);
    $('#settingsTotalSecondExams').text(loadSecondExamList().length);
    const settings = loadSettings();
    $('#settingsPortalName').val(settings.portalName);
    $('#settingsQualifyingPct').val(settings.qualifyingPct);
    $('#settingsRound1AccessCode').val(settings.round1AccessCode);
  }

  $('#settingsForm').on('submit', function (e) {
    e.preventDefault();
    const settings = {
      portalName: $('#settingsPortalName').val().trim() || 'Online Exam Portal',
      qualifyingPct: Number($('#settingsQualifyingPct').val()) || 70,
      round1AccessCode: $('#settingsRound1AccessCode').val().trim().toUpperCase() || 'WELCOME1'
    };
    saveSettings(settings);
    renderDashboard();
    renderRound2Results();
    renderRound2Invites();
    alert('Settings saved.');
  });

  $('#generateRound1CodeBtn').on('click', function () {
    $('#settingsRound1AccessCode').val(generateAccessCode());
  });

  // ---------- Registered Students (details entered on the login page) ----------
  function loadStudents() {
    try { return JSON.parse(localStorage.getItem('registeredStudents') || '[]'); } catch (e) { return []; }
  }

  function renderStudents() {
    const students = loadStudents();
    const $tbody = $('#studentsTableBody');
    $tbody.empty();
    $('#studentsCountBadge').text(students.length);

    if (students.length === 0) {
      $('#tableWrapper .table-responsive').hide();
      $('#emptyState').show();
      return;
    }
    $('#tableWrapper .table-responsive').show();
    $('#emptyState').hide();

    students.forEach(function (s, i) {
      $tbody.append(`
        <tr>
          <td>${i + 1}</td>
          <td class="name-cell">${s.fullName || s.name || '-'}</td>
          <td>${s.email || '-'}</td>
          <td>${s.phone || s.mobile || '-'}</td>
        </tr>`);
    });
  }

  // ---------- Round 2 Result (live) ----------
  function loadSecondLevelResults() {
    try { return JSON.parse(localStorage.getItem('secondLevelResults') || '[]'); } catch (e) { return []; }
  }

  function computeRound2Rows() {
    const qualifyingPct = Number(loadSettings().qualifyingPct) || 70;
    return loadSecondLevelResults().slice().reverse().map(function (r) {
      const pct = parseFloat(r.scorePct || 0);
      return {
        name: r.candidateName || 'Unknown',
        email: r.email || 'N/A',
        phone: r.phone || 'N/A',
        language: r.language || '-',
        cases: (r.totalPassed || 0) + ' / ' + (r.totalCases || 0),
        pct: isNaN(pct) ? 0 : pct,
        submittedAt: r.submittedAt,
        passed: (isNaN(pct) ? 0 : pct) >= qualifyingPct
      };
    });
  }

  function renderRound2Results() {
    const qualifyingPct = Number(loadSettings().qualifyingPct) || 70;
    $('#qualifyingPctBadge').text('≥ ' + qualifyingPct + '%');

    const rows = computeRound2Rows();
    const $tbody = $('#round2ResultTableBody');
    $tbody.empty();

    let passed = 0, failed = 0;
    rows.forEach(function (r) { r.passed ? passed++ : failed++; });
    $('#round2PassCount').text('Passed: ' + passed);
    $('#round2FailCount').text('Failed: ' + failed);

    if (rows.length === 0) {
      $('#round2ResultTableWrapper').hide();
      $('#round2ResultEmptyState').show();
      return;
    }
    $('#round2ResultTableWrapper').show();
    $('#round2ResultEmptyState').hide();

    rows.forEach(function (r) {
      const badge = r.passed
        ? `<span class="badge-pill pass"><i class="fa-solid fa-check me-1"></i> PASSED</span>`
        : `<span class="badge-pill fail"><i class="fa-solid fa-xmark me-1"></i> FAILED</span>`;
      $tbody.append(`
        <tr>
          <td class="name-cell">${r.name}</td>
          <td>${r.email}</td>
          <td>${r.phone}</td>
          <td>${String(r.language).toUpperCase()}</td>
          <td><span class="badge-pill attempted">${r.cases}</span></td>
          <td class="fw-bold">${r.pct}%</td>
          <td>${formatDate(r.submittedAt)}</td>
          <td>${badge}</td>
        </tr>`);
    });
  }

  // Real-time sync: react to other tabs writing results, and poll this tab.
  let lastRound2Snapshot = localStorage.getItem('secondLevelResults') || '';
  let lastStudentsSnapshot = localStorage.getItem('registeredStudents') || '';
  let lastSubmissionsSnapshot = localStorage.getItem('allExamSubmissions') || '';

  function syncLiveData() {
    const r2 = localStorage.getItem('secondLevelResults') || '';
    if (r2 !== lastRound2Snapshot) {
      lastRound2Snapshot = r2;
      renderRound2Results();
    }
    const st = localStorage.getItem('registeredStudents') || '';
    if (st !== lastStudentsSnapshot) {
      lastStudentsSnapshot = st;
      renderStudents();
    }
    const sub = localStorage.getItem('allExamSubmissions') || '';
    if (sub !== lastSubmissionsSnapshot) {
      lastSubmissionsSnapshot = sub;
      renderDashboard();
      renderRound2Invites();
    }
  }

  $(window).on('storage.pageAdminDash', function (e) {
    const key = e.originalEvent ? e.originalEvent.key : null;
    if (!key || key === 'secondLevelResults' || key === 'registeredStudents' || key === 'allExamResults' || key === 'allExamSubmissions') {
      renderRound2Results();
      renderStudents();
      renderDashboard();
      renderRound2Invites();
      lastRound2Snapshot = localStorage.getItem('secondLevelResults') || '';
      lastStudentsSnapshot = localStorage.getItem('registeredStudents') || '';
      lastSubmissionsSnapshot = localStorage.getItem('allExamSubmissions') || '';
    }
  });
  const syncLiveDataInterval = setInterval(syncLiveData, 2000);

  // ---------- PDF Exports ----------
  function createPdfDoc() {
    const jsPDFCtor = (window.jspdf && window.jspdf.jsPDF) || window.jsPDF;
    if (!jsPDFCtor) { alert('PDF library failed to load. Please check your connection and retry.'); return null; }
    return new jsPDFCtor({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  }

  function pdfTitle(doc, title, subtitle) {
    doc.setFontSize(16);
    doc.text(title, 40, 40);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(subtitle, 40, 58);
    doc.setTextColor(0);
  }

  function pdfTable(doc, head, body, startY) {
    doc.autoTable({
      head: [head],
      body: body,
      startY: startY,
      styles: { fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: [15, 23, 42] },
      alternateRowStyles: { fillColor: [248, 250, 252] }
    });
    return doc.lastAutoTable.finalY;
  }

  function round1PassedRows() {
    const qualifyingPct = Number(loadSettings().qualifyingPct) || 70;
    const rows = [];
    loadResults().forEach(function (r) {
      const total = r.totalQuestions || 20;
      let attempted = 0;
      if (r.userAnswers) attempted = Object.keys(r.userAnswers).length;
      else if (r.attemptedCount !== undefined) attempted = r.attemptedCount;
      const scoreVal = r.score !== undefined ? r.score : attempted;
      const pct = r.percentage !== undefined ? parseFloat(r.percentage) : (total > 0 ? Math.round((scoreVal / total) * 100) : 0);
      if (pct >= qualifyingPct) {
        rows.push([r.candidateName || r.name || 'Unknown', scoreVal + ' / ' + total, pct + '%', formatDate(r.submittedAt || r.timestamp), 'PASSED']);
      }
    });
    return rows;
  }

  function round2PassedRows() {
    return computeRound2Rows().filter(function (r) { return r.passed; })
      .map(function (r) { return [r.name, r.email, r.phone, r.cases, r.pct + '%', formatDate(r.submittedAt), 'PASSED']; });
  }

  $('#downloadStudentsPdfBtn').on('click', function () {
    const students = loadStudents();
    if (students.length === 0) { alert('No student details available to export.'); return; }
    const doc = createPdfDoc();
    if (!doc) return;
    pdfTitle(doc, 'Registered Students', 'Details submitted on the candidate login page — generated ' + new Date().toLocaleString());
    pdfTable(doc, ['#', 'Full Name', 'Email Address', 'Phone Number'],
      students.map(function (s, i) { return [i + 1, s.fullName || s.name || '-', s.email || '-', s.phone || s.mobile || '-']; }), 75);
    doc.save('students-list.pdf');
  });

  $('#downloadPassedR1Btn').on('click', function () {
    const rows = round1PassedRows();
    if (rows.length === 0) { alert('No candidates have passed Round 1 yet.'); return; }
    const doc = createPdfDoc();
    if (!doc) return;
    pdfTitle(doc, 'Passed Candidates — Round 1', 'Qualifying threshold ≥ ' + (Number(loadSettings().qualifyingPct) || 70) + '%');
    pdfTable(doc, ['Candidate Name', 'Score', 'Percentage', 'Submitted On', 'Status'], rows, 75);
    doc.save('passed-round1.pdf');
  });

  $('#downloadPassedR2Btn').on('click', function () {
    const rows = round2PassedRows();
    if (rows.length === 0) { alert('No candidates have passed Round 2 yet.'); return; }
    const doc = createPdfDoc();
    if (!doc) return;
    pdfTitle(doc, 'Passed Candidates — Round 2', 'Qualifying threshold ≥ ' + (Number(loadSettings().qualifyingPct) || 70) + '%');
    pdfTable(doc, ['Candidate Name', 'Email', 'Phone', 'Test Cases', 'Score %', 'Submitted On', 'Status'], rows, 75);
    doc.save('passed-round2.pdf');
  });

  $('#downloadPassedAllBtn').on('click', function () {
    const r1 = round1PassedRows();
    const r2 = round2PassedRows();
    if (r1.length === 0 && r2.length === 0) { alert('No passed candidates available to export yet.'); return; }
    const doc = createPdfDoc();
    if (!doc) return;
    const threshold = Number(loadSettings().qualifyingPct) || 70;
    pdfTitle(doc, 'Passed Candidates — Round 1 & Round 2', 'Qualifying threshold ≥ ' + threshold + '% — generated ' + new Date().toLocaleString());

    doc.setFontSize(12);
    doc.text('Round 1 — Passed (' + r1.length + ')', 40, 85);
    let y = pdfTable(doc, ['Candidate Name', 'Score', 'Percentage', 'Submitted On', 'Status'],
      r1.length ? r1 : [['No passed candidates', '-', '-', '-', '-']], 95);

    doc.setFontSize(12);
    doc.text('Round 2 — Passed (' + r2.length + ')', 40, y + 30);
    pdfTable(doc, ['Candidate Name', 'Email', 'Phone', 'Test Cases', 'Score %', 'Submitted On', 'Status'],
      r2.length ? r2 : [['No passed candidates', '-', '-', '-', '-', '-', '-']], y + 40);

    doc.save('passed-round1-round2.pdf');
  });

  // Initial Execution Call
  renderDashboard();
  renderStudents();
  renderRound2Results();
  renderRound2Invites();
  renderExamManagement();
  renderCategoryOptions();
  renderQBank();
  renderSecondExamManagement();
  renderCodingQBank();
  renderSettingsOverview();

  // Cleanup: this component's own on-element handlers are removed
  // automatically when React unmounts those elements, but delegated
  // document/window-level handlers and the live-sync poller are not
  // scoped to any element, so they're unbound explicitly here.
  return () => {
    clearInterval(syncLiveDataInterval);
    $(document).off('.pageAdminDash');
    $(window).off('.pageAdminDash');
  };

  }, [navigate]);

  return (
    <div className="page-admin-dashboard">
<div className="header-bar d-flex flex-wrap justify-content-between align-items-center gap-2">
  <div className="d-flex align-items-center gap-3">
    <div className="portal-title">
      <i className="fa-solid fa-user-shield me-1"></i> Online Exam Portal — Admin
    </div>
    <span className="header-datetime d-none d-md-inline" id="adminEmailDisplay"></span>
  </div>
  
  <div className="d-flex align-items-center gap-3">
    <span className="text-white small d-none d-md-inline"><i className="fa-solid fa-circle text-success me-1"></i> Live Session</span>
    <button className="btn btn-logout btn-sm" id="logoutBtnTop">
      <i className="fa-solid fa-right-from-bracket me-1"></i> Logout
    </button>
  </div>
</div>

<div className="app-shell">

  
  <div className="sidebar">
    <button className="menu-item active" data-target="dashboard"><span className="ico">🏠</span> Dashboard</button>
    <button className="menu-item" data-target="students"><span className="ico">👥</span> Students</button>
    
    
    <div className="nav-item-group w-100">
      <button className="menu-item" id="examMenuToggle">
        <span className="ico">📝</span> Exam 
        <i className="fa-solid fa-chevron-down ms-auto" style={{ fontSize: '0.75rem', color: '#64748b' }}></i>
      </button>
      <div className="sub-menu-list" style={{ display: 'none' }}>
        <button className="menu-item" data-target="examManagement"><span className="ico text-muted" style={{ fontSize: '0.5rem', marginRight: '2px' }}>⏺</span> Round 1</button>
        <button className="menu-item" data-target="round2"><span className="ico text-muted" style={{ fontSize: '0.5rem', marginRight: '2px' }}>⏺</span> Round 2</button>
      </div>
    </div>

    <button className="menu-item" data-target="resultsReport"><span className="ico">📄</span> Results Report</button>
    <button className="menu-item" data-target="settings"><span className="ico">⚙️</span> Settings</button>
    <button className="menu-item logout" id="logoutBtnSide"><span className="ico">🚪</span> Logout</button>
  </div>

  <div className="main-wrapper">

    
    <section className="page-section active" id="page-dashboard">
      <div className="section-heading">Executive Dashboard</div>
      <div className="section-sub">Comprehensive visual analytics derived from Student submissions and Results Report criteria</div>

      
      <div className="stats-row">
        <div className="stat-card" style={{ borderLeft: '4px solid #0284c7' }}>
          <div>
            <div className="stat-label">Total Candidates</div>
            <div className="stat-num" id="statTotalCandidates">0</div>
          </div>
          <div className="stat-icon candidates"><i className="fa-solid fa-users"></i></div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #16a34a' }}>
          <div>
            <div className="stat-label">Candidates Passed</div>
            <div className="stat-num text-success" id="statTotalPassed">0</div>
          </div>
          <div className="stat-icon passed"><i className="fa-solid fa-circle-check"></i></div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <div>
            <div className="stat-label">Candidates Failed</div>
            <div className="stat-num text-danger" id="statTotalFailed">0</div>
          </div>
          <div className="stat-icon failed"><i className="fa-solid fa-circle-xmark"></i></div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #d97706' }}>
          <div>
            <div className="stat-label">Questions Attempted</div>
            <div className="stat-num text-warning" id="statTotalAttempted">0</div>
          </div>
          <div className="stat-icon attempted"><i className="fa-solid fa-list-check"></i></div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #9333ea' }}>
          <div>
            <div className="stat-label">Avg. Score</div>
            <div className="stat-num" style={{ color: '#9333ea' }} id="statAvgPercent">0%</div>
          </div>
          <div className="stat-icon avg"><i className="fa-solid fa-chart-line"></i></div>
        </div>
      </div>

      
      <div className="card-box">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <h6><i className="fa-solid fa-chart-pie me-2 text-primary"></i>Visual Exam Analytics</h6>
          <span className="badge bg-light text-dark border small fw-bold" id="dashPassingBadge">Passing Criterion: ≥ 70%</span>
        </div>
        <div className="sub">Dynamic graphical charts generated from Students and Results Report data</div>
        
        <div className="chart-grid">
          
          <div className="chart-card">
            <div className="chart-title">
              <i className="fa-solid fa-circle-half-stroke me-1 text-success"></i> Pass vs. Fail Ratio
            </div>
            <div className="chart-wrap">
              <canvas id="chartPassFail"></canvas>
              <div className="donut-center">
                <span className="big" id="passRateValue">0%</span>
                <span className="small">Pass Rate</span>
              </div>
            </div>
            <div className="chart-caption" id="captionPassFail">
              <span>Passed: <b>0</b></span><span>Failed: <b>0</b></span>
            </div>
          </div>

          
          <div className="chart-card">
            <div className="chart-title">
              <i className="fa-solid fa-chart-column me-1 text-info"></i> Score Performance Brackets
            </div>
            <div className="chart-wrap"><canvas id="chartScoreDistribution"></canvas></div>
            <div className="chart-caption" id="captionScore"><span>No submissions yet</span></div>
          </div>

          
          <div className="chart-card">
            <div className="chart-title">
              <i className="fa-solid fa-bars-progress me-1 text-warning"></i> Completion Rate Ranges
            </div>
            <div className="chart-wrap"><canvas id="chartCompletionBuckets"></canvas></div>
            <div className="chart-caption" id="captionCompletion"><span>No submissions yet</span></div>
          </div>

          
          <div className="chart-card">
            <div className="chart-title">
              <i className="fa-solid fa-chart-pie me-1 text-danger"></i> Question Attempt Ratio
            </div>
            <div className="chart-wrap">
              <canvas id="chartAttemptRatio"></canvas>
              <div className="donut-center">
                <span className="big" id="attemptRateValue">0%</span>
                <span className="small">Attempted</span>
              </div>
            </div>
            <div className="chart-caption" id="captionAttempt"><span>No submissions yet</span></div>
          </div>

          
          <div className="chart-card">
            <div className="chart-title">
              <i className="fa-solid fa-filter-circle-dollar me-1 text-primary"></i> Round 1 &rarr; Round 2 Funnel
            </div>
            <div className="chart-wrap"><canvas id="chartRoundFunnel"></canvas></div>
            <div className="chart-caption" id="captionFunnel"><span>No submissions yet</span></div>
          </div>

          
          <div className="chart-card">
            <div className="chart-title">
              <i className="fa-solid fa-medal me-1 text-warning"></i> Top 5 Performing Candidates
            </div>
            <div className="chart-wrap"><canvas id="chartTopPerformers"></canvas></div>
            <div className="chart-caption" id="captionTop"><span>No submissions yet</span></div>
          </div>

          
          <div className="chart-card wide">
            <div className="chart-title">
              <i className="fa-solid fa-arrow-trend-up me-1 text-info"></i> Score Trend Across Submissions
            </div>
            <div className="chart-wrap"><canvas id="chartScoreTrend"></canvas></div>
            <div className="chart-caption" id="captionTrend"><span>No submissions yet</span></div>
          </div>
        </div>

      </div>

      
      <div className="card-box">
        <h6 className="mb-1"><i className="fa-solid fa-clock-rotate-left me-2 text-secondary"></i>Recent Candidates Evaluation</h6>
        <div className="sub mb-3">Live feed of candidates' recent results and pass/fail standings</div>
        <div className="table-responsive">
          <table className="table candidates-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Candidate Name</th>
                <th>Submitted Date</th>
                <th>Attempted Qs</th>
                <th>Score %</th>
                <th>Result Status</th>
              </tr>
            </thead>
            <tbody id="dashboardRecentTableBody">
              
            </tbody>
          </table>
        </div>
      </div>
    </section>

    
    <section className="page-section" id="page-students">
      <div className="section-heading">Students</div>
      <div className="section-sub">Details submitted by candidates on the login page</div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <h6>Registered Students <span className="badge-pill attempted" id="studentsCountBadge">0</span></h6>
            <div className="sub mb-0">Full Name, Email Address and Phone Number as entered at login</div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-sm btn-dark fw-bold" id="downloadStudentsPdfBtn">
              <i className="fa-solid fa-file-pdf me-1"></i> Download PDF
            </button>
            <button className="btn-clear" id="clearDataBtn"><i className="fa-solid fa-trash me-1"></i> Clear All Data</button>
          </div>
        </div>

        <div id="tableWrapper">
          <div className="table-responsive">
            <table className="table candidates-table mb-0 align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Full Name</th>
                  <th>Email Address</th>
                  <th>Phone Number</th>
                </tr>
              </thead>
              <tbody id="studentsTableBody"></tbody>
            </table>
          </div>
          <div className="empty-state" id="emptyState" style={{ display: 'none' }}>
            <div className="emoji">📭</div>
            <div>No student details have been submitted yet.</div>
          </div>
        </div>
      </div>
    </section>

    
    <section className="page-section" id="page-examManagement">
      <div className="section-heading">Exam - Round 1</div>
      <div className="section-sub">Create and manage Round 1 exams available on the portal</div>

      <div className="card-box">
        <h6>Create New Exam</h6>
        <div className="sub">Add a new exam to the portal</div>
        <form id="examCreateForm" className="row g-3">
          <div className="col-md-4">
            <label className="form-label small fw-bold text-secondary">Exam Name</label>
            <select className="form-select" id="examNameInput" defaultValue="" required>
              <option value="" disabled>Select exam name</option>
              <option value="Aptitude">Aptitude</option>
              <option value="Reasoning">Reasoning</option>
              <option value="Verbal">Verbal</option>
              <option value="Maths">Maths</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold text-secondary">Duration (mins)</label>
            <input type="number" className="form-control" id="examDurationInput" min="1" placeholder="30" required />
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold text-secondary">Total Questions</label>
            <input type="number" className="form-control" id="examQuestionsInput" min="1" placeholder="20" required />
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold text-secondary">Status</label>
            <select className="form-select" id="examStatusInput">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button type="submit" className="btn btn-dark w-100 fw-bold">+ Add Exam</button>
          </div>
        </form>
      </div>

      <div className="card-box">
        <h6>Existing Exams</h6>
        <div className="sub">All exams currently configured on the portal</div>
        <div className="table-responsive">
          <table className="table candidates-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Exam Name</th>
                <th>Duration</th>
                <th>Total Questions</th>
                <th>Status</th>
                <th>Created On</th>
                <th></th>
              </tr>
            </thead>
            <tbody id="examListTableBody"></tbody>
          </table>
        </div>
        <div className="empty-state" id="examListEmptyState" style={{ display: 'none' }}>
          <div className="emoji">📝</div>
          <div>No exams created yet.</div>
        </div>
      </div>

      
      <h6 className="fw-bold mt-4" style={{ color: 'var(--brand-dark)' }}>Question Bank Management</h6>
      <div className="section-sub mb-3">Add and manage MCQ questions. These sync directly to the candidate's dashboard exam.</div>
      
      <div className="card-box mb-4">
        <h6 className="fw-bold" id="qFormTitle" style={{ color: 'var(--brand-dark)' }}>Add MCQ to Database</h6>
        <form id="qBankForm" className="row g-3 mt-1">
          <input type="hidden" id="editQId" defaultValue="" />
          <div className="col-md-3">
            <label className="form-label small fw-bold text-secondary">Category</label>
            <input type="text" className="form-control" id="qCategory" list="examCategoryOptions" placeholder="e.g. Reasoning" required />
            <datalist id="examCategoryOptions"></datalist>
          </div>
          <div className="col-md-9">
            <label className="form-label small fw-bold text-secondary">Question Text</label>
            <input type="text" className="form-control" id="qText" placeholder="Question statement..." required />
          </div>
          <div className="col-md-6"><input type="text" className="form-control" id="optA" placeholder="Option A" required /></div>
          <div className="col-md-6"><input type="text" className="form-control" id="optB" placeholder="Option B" required /></div>
          <div className="col-md-6"><input type="text" className="form-control" id="optC" placeholder="Option C" required /></div>
          <div className="col-md-6"><input type="text" className="form-control" id="optD" placeholder="Option D" required /></div>
          <div className="col-md-4">
            <label className="form-label small fw-bold text-secondary">Correct Option</label>
            <select className="form-select" id="qCorrect" required>
              <option value="0">Option A</option><option value="1">Option B</option>
              <option value="2">Option C</option><option value="3">Option D</option>
            </select>
          </div>
          <div className="col-md-8 d-flex flex-column align-items-end justify-content-end">
            <button type="submit" className="btn btn-dark w-100 fw-bold" id="qSubmitBtn">+ Add Question</button>
            <div id="qSuccessMsg" className="text-success small fw-bold mt-2" style={{ display: 'none' }}></div>
          </div>
        </form>
      </div>

      
      <div className="card-box table-responsive mb-4">
        <h6 className="fw-bold mb-3 border-bottom pb-2" style={{ color: 'var(--brand-dark)' }}>Default Question Bank</h6>
        <table className="table candidates-table mb-0 align-middle">
          <thead>
            <tr>
              <th>Category</th>
              <th>Question</th>
              <th>Options</th>
              <th>Correct Ans</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody id="defaultQBankTableBody">
            
          </tbody>
        </table>
      </div>

      
      <div className="card-box mt-4 border shadow-sm round2-cq-highlight">
        <h6 className="fw-bold text-success mb-3 border-bottom pb-2"><i className="fa-solid fa-plus-circle me-2"></i>Newly Added Questions</h6>
        <div className="table-responsive">
          <table className="table candidates-table mb-0 align-middle">
            <thead className="text-muted small text-uppercase">
              <tr>
                <th className="py-3 fw-bold" style={{ width: '40%' }}>Question</th>
                <th className="py-3 fw-bold" style={{ width: '30%' }}>Options</th>
                <th className="py-3 fw-bold text-center" style={{ width: '15%' }}>Correct</th>
                <th className="py-3 fw-bold text-end" style={{ width: '15%' }}>Actions</th>
              </tr>
            </thead>
            <tbody id="newQBankTableBody">
              
            </tbody>
          </table>
        </div>
      </div>
    </section>

    
    <section className="page-section" id="page-round2">
      <div className="section-heading">Exam - Round 2 (Technical/Coding)</div>
      <div className="section-sub">Manage test cases, coding questions, and eligible candidates</div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <h6>Round 2 Invitations</h6>
            <div className="sub mb-0">
              Candidates who passed Round 1 — send each one their personal Round 2 access link and code by
              email. Round 2 only unlocks once they enter the matching code.
            </div>
          </div>
          <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fw-bold" id="round2EligibleBadge">
            Eligible: 0
          </span>
        </div>
        <div className="table-responsive">
          <table className="table candidates-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Candidate Name</th>
                <th>Email</th>
                <th>Round 1 Score</th>
                <th>Access Code</th>
                <th>Invite Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody id="round2InviteTableBody"></tbody>
          </table>
        </div>
        <div className="empty-state" id="round2InviteEmptyState" style={{ display: 'none' }}>
          <div className="emoji">📨</div>
          <div>No candidates have passed Round 1 yet.</div>
        </div>
      </div>

      
      <div className="card-box">
        <h6>Create Test Cases Exam Configuration</h6>
        <div className="sub">Set up a coding / test-cases round for shortlisted candidates</div>
        <form id="secondExamCreateForm" className="row g-3">
          <div className="col-md-4">
            <label className="form-label small fw-bold text-secondary">Exam Name</label>
            <input type="text" className="form-control" id="secondExamNameInput" placeholder="e.g. Test Cases Round 1" required />
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold text-secondary">Duration (mins)</label>
            <input type="number" className="form-control" id="secondExamDurationInput" min="1" placeholder="45" required />
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold text-secondary">Test Cases</label>
            <input type="number" className="form-control" id="secondExamCasesInput" min="1" placeholder="10" required />
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold text-secondary">Difficulty</label>
            <select className="form-select" id="secondExamDifficultyInput" defaultValue="Medium">
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button type="submit" className="btn btn-dark w-100 fw-bold">+ Add Config</button>
          </div>
        </form>
      </div>

      
      <div className="card-box">
        <h6>Round 2 Configured Exams</h6>
        <div className="sub">Test-cases based exams configured for the second round. The most recently created configuration applies to the portal.</div>
        <div className="table-responsive">
          <table className="table candidates-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Exam Name</th>
                <th>Duration</th>
                <th>Test Cases</th>
                <th>Difficulty</th>
                <th>Created On</th>
                <th></th>
              </tr>
            </thead>
            <tbody id="secondExamListTableBody"></tbody>
          </table>
        </div>
        <div className="empty-state" id="secondExamListEmptyState" style={{ display: 'none' }}>
          <div className="emoji">🎯</div>
          <div>No second level configs created yet.</div>
        </div>
      </div>

      
      <div className="card-box mt-4 border-top border-4 border-primary">
        <h6 className="fw-bold text-primary">Manage Round 2 Coding Questions</h6>
        <div className="sub">Add algorithmic problems. These sync directly to the candidate's Round 2 dashboard. Exactly 3 examples are required.</div>
        
        <form id="codingQForm" className="row g-3 mt-2">
          <input type="hidden" id="editCqId" defaultValue="" />
          <div className="col-md-6">
            <label className="form-label small fw-bold">Problem Title</label>
            <input type="text" className="form-control" id="cqTitle" placeholder="e.g. Find Max Element" required />
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-bold">Difficulty</label>
            <select className="form-select" id="cqDiff">
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
          </div>
          <div className="col-12">
            <label className="form-label small fw-bold">Problem Description</label>
            <textarea className="form-control" id="cqDesc" rows="2" placeholder="Describe the problem in detail..." required></textarea>
          </div>
          
          <div className="col-md-4">
            <label className="form-label small fw-bold">Ex 1 Input <span className="text-muted">(Comma sep)</span></label>
            <input type="text" className="form-control border-info" id="cqEx1In" placeholder="e.g. 1, 2, 3" required />
          </div>
          <div className="col-md-8">
            <label className="form-label small fw-bold">Ex 1 Output</label>
            <input type="text" className="form-control border-info" id="cqEx1Out" placeholder="e.g. 6" required />
          </div>
          
          <div className="col-md-4">
            <label className="form-label small fw-bold">Ex 2 Input</label>
            <input type="text" className="form-control border-warning" id="cqEx2In" placeholder="e.g. -1, -2" required />
          </div>
          <div className="col-md-8">
            <label className="form-label small fw-bold">Ex 2 Output</label>
            <input type="text" className="form-control border-warning" id="cqEx2Out" placeholder="e.g. -3" required />
          </div>
          
          <div className="col-md-4">
            <label className="form-label small fw-bold">Ex 3 Input</label>
            <input type="text" className="form-control border-danger" id="cqEx3In" placeholder="e.g. 10, 20" required />
          </div>
          <div className="col-md-8">
            <label className="form-label small fw-bold">Ex 3 Output</label>
            <input type="text" className="form-control border-danger" id="cqEx3Out" placeholder="e.g. 30" required />
          </div>

          <div className="col-12">
            <label className="form-label small fw-bold">Correct Output</label>
            <input type="text" className="form-control" id="cqCorrectOutput" placeholder="Expected final correct output" required />
          </div>

          <div className="col-12 d-flex justify-content-end mt-4">
            <button type="submit" className="btn btn-dark fw-bold px-5">+ Add Coding Problem</button>
          </div>
        </form>
      </div>

      
      <div className="card-box">
         <h6 className="fw-bold text-secondary border-bottom pb-2">Round 2 Question Bank</h6>
         <div className="table-responsive">
           <table className="table candidates-table mb-0 align-middle">
             <thead><tr><th>Title</th><th>Difficulty</th><th>Correct Output</th><th>Examples (3 Required)</th><th style={{ minWidth: '140px' }}>Action</th></tr></thead>
             <tbody id="codingQTableBody"></tbody>
           </table>
         </div>
      </div>
    </section>

    
    <section className="page-section" id="page-resultsReport">
      <div className="section-heading">Results Report</div>
      <div className="section-sub">Detailed report of every submission including Pass/Fail evaluation criteria</div>
      
      
      <div className="alert bg-white border shadow-sm d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4 p-3 rounded-3">
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 bg-dark text-white rounded-2"><i className="fa-solid fa-calculator fs-5"></i></div>
          <div>
            <h6 className="fw-bold mb-0">Evaluation Rule & Pass Criteria</h6>
            <span className="small text-muted">Qualifying Threshold: <strong className="text-primary" id="reportQualifyingThresholdText">≥ 70% Score</strong></span>
          </div>
        </div>
        <div className="d-flex gap-2">
          <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fw-bold" id="reportPassBadgeCount"><i className="fa-solid fa-check-circle me-1"></i> Passed: 0</span>
          <span className="badge bg-danger-subtle text-danger border border-danger px-3 py-2 fw-bold" id="reportFailBadgeCount"><i className="fa-solid fa-xmark-circle me-1"></i> Failed: 0</span>
        </div>
      </div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <h6>Round 1 Result</h6>
            <div className="sub mb-0">MCQ round submissions with Pass/Fail evaluation</div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-sm btn-dark fw-bold" id="downloadPassedR1Btn">
              <i className="fa-solid fa-file-pdf me-1"></i> Passed (Round 1) PDF
            </button>
            <button className="btn btn-sm btn-outline-dark fw-bold" id="downloadPassedAllBtn">
              <i className="fa-solid fa-file-pdf me-1"></i> Passed (Round 1 + Round 2) PDF
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table candidates-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Candidate Name</th>
                <th>Submitted On</th>
                <th>Attempted</th>
                <th>Not Attempted</th>
                <th>Total Questions</th>
                <th>Score Marks</th>
                <th>Percentage</th>
                <th>Status / Criteria</th>
              </tr>
            </thead>
            <tbody id="reportTableBody"></tbody>
          </table>
        </div>
        <div className="empty-state" id="reportEmptyState" style={{ display: 'none' }}>
          <div className="emoji">📭</div>
          <div>No results to report yet.</div>
        </div>
      </div>

      
      <div className="card-box">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <h6>Round 2 Result
              <span className="badge-pill pass" id="qualifyingPctBadge">≥ 70%</span>
              <span className="badge bg-success-subtle text-success border border-success ms-1 small"><i className="fa-solid fa-circle fa-2xs me-1"></i> Live</span>
            </h6>
            <div className="sub mb-0">Updates in real time as candidates attempt and submit the Round 2 coding exam</div>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fw-bold" id="round2PassCount">Passed: 0</span>
            <span className="badge bg-danger-subtle text-danger border border-danger px-3 py-2 fw-bold" id="round2FailCount">Failed: 0</span>
            <button className="btn btn-sm btn-dark fw-bold" id="downloadPassedR2Btn">
              <i className="fa-solid fa-file-pdf me-1"></i> Passed (Round 2) PDF
            </button>
          </div>
        </div>
        <div className="table-responsive" id="round2ResultTableWrapper">
          <table className="table candidates-table mb-0 align-middle">
            <thead>
              <tr>
                <th>Candidate Name</th>
                <th>Email Address</th>
                <th>Phone Number</th>
                <th>Language</th>
                <th>Test Cases Passed</th>
                <th>Score %</th>
                <th>Submitted On</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody id="round2ResultTableBody"></tbody>
          </table>
        </div>
        <div className="empty-state" id="round2ResultEmptyState" style={{ display: 'none' }}>
          <div className="emoji">🎯</div>
          <div>No candidate has submitted the Round 2 exam yet.</div>
        </div>
      </div>
    </section>

    
    <section className="page-section" id="page-settings">
      <div className="section-heading">Settings</div>
      <div className="section-sub">Admin account and portal preferences</div>

      <div className="card-box">
        <h6>Overview</h6>
        <div className="sub">Read-only portal statistics</div>
        <div className="detail-grid">
          <div className="detail-field">
            <div className="k">Admin Email</div>
            <div className="v" id="settingsAdminEmail">-</div>
          </div>
          <div className="detail-field">
            <div className="k">Total Candidates Stored</div>
            <div className="v" id="settingsTotalCandidates">0</div>
          </div>
          <div className="detail-field">
            <div className="k">Total Exams Created</div>
            <div className="v" id="settingsTotalExams">0</div>
          </div>
        </div>
      </div>

      <div className="card-box">
        <h6>Portal Preferences</h6>
        <div className="sub">Configure how the portal behaves</div>
        <form id="settingsForm" className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label small fw-bold text-secondary">Portal Name</label>
            <input type="text" className="form-control" id="settingsPortalName" placeholder="Online Exam Portal" />
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-bold text-secondary">Qualifying % for Second Level & Pass Status</label>
            <input type="number" className="form-control" id="settingsQualifyingPct" min="0" max="100" placeholder="70" />
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-bold text-secondary">
              Round 1 Access Code <i className="fa-solid fa-circle-question text-muted" title="Candidates enter this on the login page instead of a password."></i>
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control text-uppercase fw-bold"
                id="settingsRound1AccessCode"
                placeholder="WELCOME1"
              />
              <button type="button" className="btn btn-outline-secondary" id="generateRound1CodeBtn" title="Generate a random code">
                <i className="fa-solid fa-shuffle"></i>
              </button>
            </div>
          </div>
          <div className="col-md-2">
            <button type="submit" className="btn btn-dark w-100 fw-bold">Save Settings</button>
          </div>
          <div className="col-12">
            <div className="small text-muted">
              Candidates enter the Round 1 Access Code on the login page, in place of a password, to reach
              Instructions / Dashboard / Summary. Share it with approved candidates however you like — after
              generating or editing it, click <strong>Save Settings</strong> to apply it.
            </div>
          </div>
        </form>
      </div>
    </section>

  </div>
</div>
    </div>
  );
}